import { PlatformGlyph } from '@/features/manager-breaks-demo/components/PlatformGlyph';
import {
  PLATFORM_TEXT,
  SEVERITY_BAR,
} from '@/features/manager-breaks-demo/components/break-styles';
import type { BreakSeverity } from '@/features/manager-breaks-demo/types/breaks';
import { OwnerAvatar } from '@/features/manager-command-center/components/OwnerAvatar';
import type { BreakingCommitment } from '../types/project';

interface BreakingCommitmentsColumnProps {
  commitments: BreakingCommitment[];
  /** Opens the full examine view for a commitment's underlying break. */
  onDeepDive: (breakId: string) => void;
}

/** Tag chip tone by severity - rose for breaking, amber for slipping. */
const TAG_TONE: Record<BreakSeverity, string> = {
  high: 'bg-rose-50 text-rose-700 ring-rose-200',
  medium: 'bg-amber-50 text-amber-700 ring-amber-200',
  low: 'bg-zinc-100 text-zinc-600 ring-zinc-200',
};

/** Live status dot - pulses on the critical ones. */
function LiveDot({ severity }: { severity: BreakSeverity }) {
  const color = severity === 'medium' ? 'bg-amber-500' : 'bg-rose-500';
  if (severity === 'high') {
    return (
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-75" />
        <span
          className={`relative inline-flex h-2 w-2 rounded-full ${color}`}
        />
      </span>
    );
  }
  return <span className={`h-2 w-2 rounded-full ${color}`} />;
}

function BreakingCommitmentCard({
  commitment,
  onDeepDive,
}: {
  commitment: BreakingCommitment;
  onDeepDive: (breakId: string) => void;
}) {
  const openDeepDive = () => onDeepDive(commitment.breakId);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={openDeepDive}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openDeepDive();
        }
      }}
      aria-label={`Deep dive into ${commitment.ref}`}
      className="group relative cursor-pointer overflow-hidden rounded-2xl border border-zinc-100 bg-white p-5 text-start outline-none shadow-[0_18px_44px_-30px_rgba(16,27,58,0.45)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_26px_54px_-26px_rgba(244,63,94,0.4)] focus-visible:ring-2 focus-visible:ring-[#00AEEF]/50"
    >
      <span
        className={`pointer-events-none absolute inset-x-0 top-0 h-1 ${
          SEVERITY_BAR[commitment.severity]
        }`}
      />

      {/* Tag + live dot */}
      <div className="mb-3 flex items-center justify-between">
        <span
          className={`rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ring-inset ${
            TAG_TONE[commitment.severity]
          }`}
        >
          {commitment.tag}
        </span>
        <div className="flex items-center gap-2">
          <LiveDot severity={commitment.severity} />
          <span className="font-mono text-[11px] font-medium text-zinc-500">
            {commitment.ref}
          </span>
        </div>
      </div>

      {/* Title + detail */}
      <h4 className="text-sm font-semibold leading-snug text-black transition-colors group-hover:text-[#0052CC]">
        {commitment.title}
      </h4>
      <p className="mt-2 text-xs leading-relaxed text-zinc-600">
        {commitment.detail}
      </p>

      {/* Metric */}
      <div className="mt-4 flex items-end justify-between border-t border-zinc-100 pt-3">
        <div>
          <span className="block text-[10px] font-medium uppercase tracking-wide text-zinc-400">
            Now
          </span>
          <span className="text-sm font-bold text-rose-600">
            {commitment.currentMetric}
            <span className="ms-1.5 text-[11px] font-normal text-zinc-400">
              {commitment.targetMetric}
            </span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            {commitment.platforms.map((platform) => (
              <span
                key={platform}
                className="flex h-6 w-6 items-center justify-center rounded-full border border-zinc-100 bg-white shadow-sm"
              >
                <PlatformGlyph
                  platform={platform}
                  className={`h-3 w-3 ${PLATFORM_TEXT[platform]}`}
                />
              </span>
            ))}
          </div>
          <OwnerAvatar
            owner={commitment.owner}
            className="h-7 w-7 text-[10px]"
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Commitments breaking right now in the project - the live, urgent items. Each
 * deep dives into the same examine view the command center and gallery use, so
 * the three surfaces tell one continuous story.
 */
export function BreakingCommitmentsColumn({
  commitments,
  onDeepDive,
}: BreakingCommitmentsColumnProps) {
  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.14em] text-zinc-500">
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-rose-50 text-rose-600">
            <svg
              viewBox="0 0 24 24"
              className="h-3 w-3"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" />
            </svg>
          </span>
          Breaking now
        </h3>
        <span
          className={`inline-flex items-center gap-1.5 rounded-lg px-2 py-0.5 text-xs font-semibold ring-1 ring-inset ${
            commitments.length === 0
              ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
              : 'bg-rose-50 text-rose-600 ring-rose-200'
          }`}
        >
          {commitments.length === 0
            ? 'all clear'
            : `${commitments.length} live`}
        </span>
      </div>

      {commitments.length === 0 ? (
        <div className="flex items-center gap-3 rounded-2xl border border-emerald-100 bg-emerald-50/40 p-5 text-sm text-emerald-700">
          <svg
            viewBox="0 0 24 24"
            className="h-5 w-5 shrink-0"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M5 13l4 4L19 7" />
          </svg>
          No commitments breaking — nothing needs a decision.
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {commitments.map((commitment) => (
            <BreakingCommitmentCard
              key={commitment.id}
              commitment={commitment}
              onDeepDive={onDeepDive}
            />
          ))}
        </div>
      )}
    </div>
  );
}
